import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

/*
  그리드 셀 안의 가로 막대(비율/달성률/증감률) 공용 렌더러.

  왜 DOM 인가: 예전엔 ag-grid 의 `agSparklineCellRenderer`(ag-charts) 를 썼다. 그건 **canvas** 라서
  CSS 를 못 탄다 — 폰트는 getFontFamily() 로 문자열을 따로 넘겨야 했고, 라벨 색은 'black' 하드코딩이라
  다크모드에서 깨졌고, 색도 테마·팔레트 값을 hex 로 계산해 넘겨야 했다. 막대 하나를 그리자고
  차트 엔진을 셀마다 띄울 이유가 없다. div 3개면 같은 그림이 나오고 CSS 를 그대로 상속한다.

  색을 안 주면 **활성 차트 팔레트의 대표색 한 가지**다(ColorUtil.colorSet(0)) — 행이 몇 개든 같은 색.
  회색·검정은 쓰지 않는다: 어느 팔레트에도 없는 색이라 "팔레트를 골랐는데 여기만 안 먹었다"로 읽힌다.
  행마다 색을 바꾸지도 않는다: 이 막대는 같은 측정치를 행끼리 비교하라고 그리는 것이라, 색을 돌리면
  길이가 아니라 색이 의미를 가진 것처럼 읽힌다.
  color 는 **막대 색이 값의 의미나 다른 시각요소와 묶여야 할 때** 준다
  (예: 증감의 감소는 위험색, DW 대시보드는 지도 마커와 맞춘 행별 색 — components/dashboard/dashboardGrid).

  cellRendererParams
    color          막대 색. 문자열 또는 (value) => 문자열. 없으면(또는 함수가 null 을 주면) 팔레트 대표색
    min / max      값의 도메인. 기본 0~100. **min 이 음수면 0 을 기준으로 좌우로 뻗는 발산형**이 된다
    formatter      라벨 텍스트. **valueFormatter 와 같은 규약** — ({ value }) => string. 없으면 `${value}%`
    labelPlacement "inside"(막대 안 왼쪽·기본) | "right"(막대 밖 오른쪽) | "none"
    barHeight      막대 두께(px). 기본 14 ("right" 는 8 이 어울린다)

  값이 null/undefined 면 빈 칸으로 둔다(비율이 없는 행 — 예: MP 시나리오 비교의 기간·재고 행).
*/
const clampPct = (v) => Math.min(Math.max(v, 0), 100);

const RatioBarCell = (p) => {
  const {
    value, color, min = 0, max = 100, formatter,
    labelPlacement = "inside", barHeight = 14,
  } = p;

  const v = Number(value);
  if (value == null || Number.isNaN(v)) {
    return null;
  }

  const span = (max - min) || 1;
  // 차트보다 한 번 더 톤을 낮춘다 — 행마다 반복되는 데다 라벨이 그 위에 얹히는 자리라
  // 차트와 같은 채도로 칠하면 표 전체가 시끄러워진다. 지정색·팔레트색 모두 같은 폭으로 낮춰야 섞여도 균일하다.
  const given = typeof color === "function" ? color(v) : color;
  const barColor = ColorUtil.soften(given || ColorUtil.colorSet(0), 0.3);
  const label = formatter ? formatter({ ...p, value: v }) : `${v}%`;

  // 발산형(min<0): 0 의 위치를 기준선으로 두고 좌우로 뻗는다. 아니면 왼쪽 끝에서 채운다.
  //   발산형에서만 부호를 방향으로 읽는다 — 아닐 땐 도메인 아래 값은 그냥 0 이다(abs 를 쓰면 반대로 그려진다).
  const zeroPct = min < 0 ? clampPct(((0 - min) / span) * 100) : 0;
  const rawPct = ((min < 0 ? Math.abs(v) : v - min) / span) * 100;
  const sizePct = Math.min(Math.max(rawPct, 0), min < 0 && v < 0 ? zeroPct : 100 - zeroPct);
  const startPct = min < 0 && v < 0 ? zeroPct - sizePct : zeroPct;

  const bar = (
    <Box sx={{
      position: "relative", flex: 1, minWidth: 0, height: barHeight,
      bgcolor: "action.hover", borderRadius: 0.5, overflow: "hidden",
      // sparkline 의 highlightedItem(노란색 덮어쓰기)을 대신한다 — 색을 바꾸지 않고 밝기만 올린다
      "&:hover .ratio-bar-fill": { filter: "brightness(1.12)" },
    }}>
      {/* 행마다 달라지는 값(위치·너비·색)은 sx 가 아니라 style 로 준다 —
          sx 는 값이 다를 때마다 emotion 클래스를 새로 만들어서, 행이 수백 개인 그리드에선 그게 곧 비용이다. */}
      <Box className="ratio-bar-fill" data-testid="ratio-bar-fill"
        style={{ left: `${startPct}%`, width: `${sizePct}%`, backgroundColor: barColor }}
        sx={{ position: "absolute", top: 0, bottom: 0, transition: "filter .12s" }} />
      {labelPlacement === "inside" && (
        <Typography sx={{
          position: "absolute", left: 4, top: 0, right: 4,
          lineHeight: `${barHeight}px`, fontSize: FS.CAPTION, fontWeight: 700,
          color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>{label}</Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%", width: "100%" }}>
      {bar}
      {labelPlacement === "right" && (
        <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", minWidth: 34, textAlign: "right" }}>{label}</Typography>
      )}
    </Box>
  );
};

export default RatioBarCell;
